import {
    ClassSpotsLeft,
    isInstanceSelected,
} from '~/components/forms/questions/scheduling/ClassDescription.jsx';
import { minutesToDuration } from '~/lib/DateTime.js';
import { truncate } from '~/lib/Utils.js';
import { CheckCircleIcon } from '@heroicons/react/24/solid/index.js';
import { useIsWaitlistEnabled } from '~/components/waitlist/AddToWaitlistModal.jsx';
import {
    clientFormatDateLong,
    clientFormatTime,
} from '~/public/lib/ClientDateTime.js';
import { FormPart, FormStateName, part, state } from '~/lib/FormCssApi';

const SingleClassBlock = ({
    instance,
    onClick,
    settings,
    selectedClasses,
    clientTz,
    showDate,
    isLarge,
}) => {
    const hasImage = !!instance.event_type.image;
    const isWaitlistEnabled = useIsWaitlistEnabled(settings);

    const cannotBeBooked = instance.spots_left <= 0 && !isWaitlistEnabled;
    const textSize = isLarge ? 'text-base' : 'text-sm';
    const isSelected = isInstanceSelected(instance, selectedClasses);

    return (
        <div
            {...part(FormPart.CLASS_BLOCK)}
            {...state(
                isSelected && FormStateName.SELECTED,
                instance.spots_left <= 0 && FormStateName.FULL,
            )}
            className={`${textSize} flex flex-col justify-between border ${cannotBeBooked ? 'opacity-75' : ''} ${isSelected ? 'themed-border' : 'border-transparent'} hover-themed-border rounded-lg mt-4 cursor-pointer`}
            onClick={onClick}
        >
            <div className="bg-white rounded-lg">
                {hasImage && (
                    <div className='relative'>
                        <img
                            {...part(FormPart.CLASS_IMAGE)}
                            src={`/view-image/${instance.event_type.image}`}
                            alt={instance.event_type.name}
                            className={`w-full ${isLarge ? 'h-32' : 'h-24'} object-cover rounded-t-lg`}
                        />
                        <div
                            className="pointer-events-none absolute inset-x-0 bottom-0 h-1/2 after:absolute after:inset-0 after:bg-gradient-to-t after:from-white after:via-white/0 after:to-transparent"
                        />
                    </div>
                )}
                <div
                    className={`${isLarge ? 'min-h-[100px]' : 'min-h-[80px]'} rounded-b-lg  ${hasImage ? 'pb-3' : 'rounded-t-lg py-3'}`}
                >
                    <div
                        className={`${hasImage ? 'bg-gradient-fade-white-b-alt pt-2' : ''} ${isLarge ? 'px-3' : 'px-2'} flex flex-col justify-between mb-3 ${textSize}`}
                    >
                        {showDate && (
                            <div
                                {...part(FormPart.CLASS_TIME)}
                                className="text-gray-500 leading-tight"
                            >
                                {clientFormatDateLong(instance.start, clientTz)}
                            </div>
                        )}
                        <div
                            {...part(FormPart.CLASS_TIME)}
                            className="font-medium mr-1"
                        >
                            {clientFormatTime(instance.start, clientTz)}
                        </div>
                        {!settings?.hideDuration && (
                            <div
                                {...part(FormPart.CLASS_DURATION)}
                                className="text-nowrap"
                            >
                                {minutesToDuration(
                                    instance.event_type.duration,
                                    true,
                                )}
                            </div>
                        )}
                    </div>
                    <div
                        {...part(FormPart.CLASS_TITLE)}
                        className={`mb-3 ${isLarge ? 'px-3' : 'px-2'} break-words`}
                    >
                        {truncate(instance.event_type.name, isLarge ? 45 : 30)}
                    </div>
                    <div
                        className={`flex items-end justify-between ${isLarge ? 'px-3' : 'px-2'}`}
                    >
                        <div>
                            <div
                                {...part(FormPart.CLASS_RESOURCE)}
                                className="text-gray-500"
                            >
                                {!settings?.hideBookableResource &&
                                    instance.bookable_resource?.name}
                            </div>
                            <ClassSpotsLeft
                                instance={instance}
                                settings={settings}
                                className="leading-tight"
                                textSize={textSize}
                            />
                        </div>
                        {isSelected && (
                            <CheckCircleIcon className="themed-text w-5 h-5" />
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SingleClassBlock;
